import { sum } from 'lodash';
import { sanatiseHealthCardNumber } from './healthfund-cards';

export interface IMedicareNumber {
  /** The ten-digit card number, without the IRN. */
  number: string;
  irn?: string;
}

/** Ten digits starting 2 to 6, optionally followed by the IRN. */
const medicareNumberPattern = /^[2-6]\d{9,10}$/;
/** Practices often record the IRN zero-padded, such as "03". */
const irnPattern = /^0*([1-9])$/;
const checkDigitWeights = [1, 3, 7, 9, 1, 3, 7, 9];
const checkDigitModulus = 10;
const checkDigitIndex = 8;
const issueNumberIndex = 9;
const irnIndex = 10;
const cardNumberLength = 10;
/** A reissued card keeps every digit before its issue number. */
const cardIdentifierLength = 9;

/**
 * A Medicare card number as Appendix E of the Australian Digital Health
 * Agency's Healthcare Identifiers Service Conformance Profile (DH-4167:2025)
 * defines it, including its check digit weights: eight digits, a check digit,
 * then an issue number that goes up each time the card is reissued. The
 * eleven-digit form adds the patient's Individual Reference Number (IRN).
 */
export class MedicareNumber {
  static isValid(value?: string): boolean {
    return !!MedicareNumber.parse(value);
  }

  static isSameCard(value?: string, otherValue?: string): boolean {
    const medicareNumber = MedicareNumber.parse(value);
    const otherMedicareNumber = MedicareNumber.parse(otherValue);
    if (!medicareNumber || !otherMedicareNumber) {
      return false;
    }
    return (
      MedicareNumber._cardIdentifier(medicareNumber.number) ===
      MedicareNumber._cardIdentifier(otherMedicareNumber.number)
    );
  }

  static parse(value?: string): IMedicareNumber | undefined {
    const digits = sanatiseHealthCardNumber(value ?? '');
    if (
      !medicareNumberPattern.test(digits) ||
      !MedicareNumber._hasValidCheckDigit(digits)
    ) {
      return;
    }
    return {
      number: digits.slice(0, cardNumberLength),
      irn: MedicareNumber.parseIrn(digits.charAt(irnIndex)),
    };
  }

  static parseIrn(value?: string): string | undefined {
    return irnPattern.exec(value?.trim() ?? '')?.[1];
  }

  static issueNumber(value?: string): number | undefined {
    const medicareNumber = MedicareNumber.parse(value);
    if (!medicareNumber) {
      return;
    }
    return Number(medicareNumber.number.charAt(issueNumberIndex));
  }

  private static _hasValidCheckDigit(digits: string): boolean {
    const weightedSum = sum(
      checkDigitWeights.map(
        (weight, index) => Number(digits.charAt(index)) * weight
      )
    );
    return (
      weightedSum % checkDigitModulus === Number(digits.charAt(checkDigitIndex))
    );
  }

  private static _cardIdentifier(cardNumber: string): string {
    return cardNumber.slice(0, cardIdentifierLength);
  }
}
